import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import "./stock-report.css";
import { fetchProducts } from "../Redux/Actions/productVActions";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from 'xlsx';

const StockReportV = () => {
  const dispatch = useDispatch();
  const componentRef = useRef();

  const [stockData, setStockData] = useState([]);
  const [groupedStock, setGroupedStock] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedLocationName, setSelectedLocationName] = useState("Toutes les localisations");
  const [lastUpdateTime, setLastUpdateTime] = useState(null);

  const productsData = useSelector(
    (state) => state.productVReducer?.products || []
  );

  // Fonction: Charger les données de stock
  const fetchStockData = useCallback(async () => {
    try {
      console.log("🔄 Chargement des données de stock...");
      const res = await axios.get("/api/StockV/stock-report");
      const stockArray = Array.isArray(res.data) ? res.data : [];
      setStockData(stockArray);
      
      // Consolider et grouper les données
      const consolidated = consolidateStockData(stockArray);
      const grouped = groupByProductSubProduct(consolidated);
      setGroupedStock(grouped);
      
      setLastUpdateTime(new Date());
      
      console.log("✅ Stock mis à jour:", {
        original: stockArray.length,
        consolidated: consolidated.length,
        grouped: grouped.length,
        time: new Date().toLocaleTimeString()
      });
      
      return stockArray;
    } catch (error) {
      console.error("❌ Erreur chargement stock :", error);
      setStockData([]);
      setGroupedStock([]);
      throw error;
    }
  }, []);

  // Fonction: Regrouper et consolider les stocks
  const consolidateStockData = useCallback((stockItems) => {
    const consolidatedMap = new Map();
    
    stockItems.forEach(item => {
      const productId = idOf(item.productV);
      const subProductId = idOf(item.subProductV);
      const locationId = idOf(item.localisation) || idOf(item.productV?.localisation) || idOf(item.subProductV?.localisation);
      
      const key = `${productId}-${subProductId}-${locationId}`;
      
      const quantity = parseFloat(item.quantity) || 0;
      const totalQuantity = parseFloat(item.totalQuantity) || 0;
      
      // Utiliser quantity en priorité pour la cohérence avec les autres composants
      const effectiveQuantity = (item.quantity !== undefined && item.quantity !== null) ? quantity : totalQuantity;
      
      if (consolidatedMap.has(key)) {
        const existing = consolidatedMap.get(key);
        existing.quantity += effectiveQuantity;
        
        if (item.lastUpdated && (!existing.lastUpdated || new Date(item.lastUpdated) > new Date(existing.lastUpdated))) {
          existing.lastUpdated = item.lastUpdated;
        }
      } else {
        consolidatedMap.set(key, {
          ...item,
          quantity: effectiveQuantity,
          consolidated: true,
          productId,
          subProductId,
          locationId,
          key
        });
      }
    });
    
    return Array.from(consolidatedMap.values()).filter(item => item.quantity > 0);
  }, []);

  // Fonction: Regrouper par produit/sous-produit pour l'affichage
  const groupByProductSubProduct = useCallback((consolidatedItems) => {
    const groupedMap = new Map();
    
    consolidatedItems.forEach(item => {
      const productId = item.productId;
      const subProductId = item.subProductId;
      const key = `${productId}-${subProductId}`;
      
      const productName = getProductName(productId, item);
      const subProductName = getSubProductName(subProductId, item);
      
      if (groupedMap.has(key)) {
        const existing = groupedMap.get(key);
        existing.totalQuantity += item.quantity;
        
        console.log(`➕ Ajout à ${productName}/${subProductName}: +${item.quantity} (Total temporaire: ${existing.totalQuantity})`);
        
        const locationName = getLocationName(item);
        if (!existing.locations.includes(locationName)) {
          existing.locations.push(locationName);
        }
        
        if (item.lastUpdated && (!existing.lastUpdated || new Date(item.lastUpdated) > new Date(existing.lastUpdated))) {
          existing.lastUpdated = item.lastUpdated;
        }
      } else {
        console.log(`🆕 Premier enregistrement pour ${productName}/${subProductName}: ${item.quantity}`);
        groupedMap.set(key, {
          productId,
          subProductId,
          productName,
          subProductName,
          barcode: item.subProductV?.barcode || "—",
          category: getCategory(item),
          unit: item.unit || "Pièces",
          totalQuantity: item.quantity,
          locations: [getLocationName(item)],
          lastUpdated: item.lastUpdated,
          subProductsDetails: [{
            id: item._id,
            quantity: item.quantity,
            location: getLocationName(item),
            lastUpdated: item.lastUpdated
          }]
        });
      }
    });
    
    console.log("🏁 Résumé groupé (StockReportV):", Array.from(groupedMap.entries()).map(([k, v]) => `${v.productName}/${v.subProductName}: ${v.totalQuantity}`));
    
    return Array.from(groupedMap.values());
  }, []);

  // Fonction: Impression PDF avec jsPDF
  const handleExportPDF = () => {
    const doc = new jsPDF('landscape');
    const date = new Date().toLocaleDateString();
    const time = lastUpdateTime ? lastUpdateTime.toLocaleTimeString() : 'N/A';
    
    // Titre
    doc.setFontSize(18);
    doc.text("📦 Rapport de Stock Consolidé", 14, 15);
    
    // Informations
    doc.setFontSize(10);
    doc.text(`Localisation : ${selectedLocationName}`, 14, 25);
    doc.text(`Date : ${date}`, 14, 30);
    doc.text(`Dernière MAJ : ${time}`, 14, 35);
    doc.text(`Total produits : ${groupedStock.length}`, 14, 40);
    
    // Préparer les données du tableau
    const tableData = groupedStock.map(item => {
      const isOutOfStock = item.totalQuantity === 0;
      const isLowStock = item.totalQuantity <= 2;
      
      return [
        item.category,
        item.barcode || "—",
        item.productName,
        item.subProductName || "—",
        item.totalQuantity.toFixed(2),
        item.unit,
        item.locations.join(', '),
        item.lastUpdated ? new Date(item.lastUpdated).toLocaleDateString() : "N/A",
        isOutOfStock ? 'Épuisé' : isLowStock ? 'Stock bas' : 'En stock'
      ];
    });
    
    // Configuration du tableau
    doc.autoTable({
      head: [
        ['Catégorie', 'Code à barre', 'Produit', 'Sous-produit', 'Quantité Totale', 'Unité', 'Localisations', 'Dernière Mise à Jour', 'Statut']
      ],
      body: tableData,
      startY: 45,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246] },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 'auto' },
        4: { cellWidth: 'auto' },
        5: { cellWidth: 'auto' },
        6: { cellWidth: 'auto' },
        7: { cellWidth: 'auto' },
        8: { cellWidth: 'auto' }
      },
      didParseCell: function(data) {
        if (data.section === 'body') {
          // Colorer les lignes de stock bas et épuisé
          const quantity = parseFloat(data.row.cells[4].text);
          if (quantity === 0) {
            data.cell.styles.textColor = [220, 53, 69]; // Rouge pour épuisé
          } else if (quantity <= 2) {
            data.cell.styles.fillColor = [255, 243, 205]; // Jaune clair pour stock bas
          }
        }
      }
    });
    
    // Pied de page
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(
        `Page ${i} sur ${pageCount}`,
        doc.internal.pageSize.width - 30,
        doc.internal.pageSize.height - 10
      );
    }
    
    // Télécharger le PDF
    doc.save(`Rapport-Stock-${date.replace(/\//g, '-')}.pdf`);
  };

  // Fonction: Exporter en Excel avec XLSX
  const handleExportExcel = () => {
    try {
      // Map groupedStock data to Excel-friendly rows
      const dataToExport = groupedStock.map(item => {
        const isLowStock = item.totalQuantity <= 2;
        const isOutOfStock = item.totalQuantity === 0;
        const status = isOutOfStock ? 'Épuisé' : isLowStock ? 'Stock bas' : 'En stock';
        
        return {
          "Catégorie": item.category,
          "Code à barre": item.barcode || "—",
          "Produit": item.productName,
          "Sous-produit": item.subProductName || "—",
          "Quantité": item.totalQuantity,
          "Unité": item.unit,
          "Localisations": item.locations.join(', '),
          "Dernière MAJ": item.lastUpdated ? new Date(item.lastUpdated).toLocaleDateString() : "N/A",
          "Statut": status
        };
      });

      // Create Worksheet
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      
      // Auto-fit columns
      const cols = [];
      if (dataToExport.length > 0) {
        for (const k in dataToExport[0]) {
          let maxLen = k.length;
          dataToExport.forEach(row => {
            const val = String(row[k] || "");
            if (val.length > maxLen) maxLen = val.length;
          });
          cols.push({ wch: maxLen + 3 });
        }
        worksheet['!cols'] = cols;
      }

      // Create Workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Consolidé");
      
      // Write file
      const cleanLocName = selectedLocationName.replace(/[^a-zA-Z0-9]/g, '_');
      const fileName = `Rapport_Stock_${cleanLocName}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error("❌ Erreur lors de l'export Excel:", error);
      alert("Erreur lors de l'export Excel");
    }
  };

  // Fonction: Impression HTML
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Rapport de Stock Consolidé</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #333;
            }
            .print-header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 2px solid #3b82f6;
              padding-bottom: 15px;
            }
            .print-header h2 {
              margin: 0 0 10px 0;
              color: #1f2937;
              font-size: 18px;
            }
            .print-info {
              display: flex;
              justify-content: center;
              gap: 30px;
              margin-bottom: 15px;
              font-size: 12px;
            }
            .print-info span {
              white-space: nowrap;
            }
            .stock-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 10px;
              margin-top: 10px;
            }
            .stock-table th {
              background-color: #3b82f6;
              color: white;
              padding: 8px 6px;
              border: 1px solid #ddd;
              text-align: left;
              font-weight: bold;
            }
            .stock-table td {
              padding: 6px 5px;
              border: 1px solid #ddd;
            }
            .stock-table tr:nth-child(even) {
              background-color: #f8f9fa;
            }
            .warning-row {
              background-color: #fff3cd !important;
            }
            .out-of-stock {
              color: #dc3545;
              font-weight: bold;
            }
            .in-stock {
              color: #28a745;
            }
            @media print {
              body { margin: 0; }
              .stock-table { page-break-inside: auto; }
              .stock-table tr { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h2>📦 Rapport de Stock Consolidé</h2>
            <div class="print-info">
              <span><strong>Localisation :</strong> ${selectedLocationName}</span>
              <span><strong>Date :</strong> ${new Date().toLocaleDateString()}</span>
              <span><strong>Dernière MAJ :</strong> ${lastUpdateTime ? lastUpdateTime.toLocaleTimeString() : 'N/A'}</span>
              <span><strong>Total produits :</strong> ${groupedStock.length}</span>
            </div>
          </div>
          ${generateTableHTML()}
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    printWindow.onload = () => {
      printWindow.print();
      printWindow.onafterprint = () => {
        printWindow.close();
      };
    };
  };

  // Générer le HTML du tableau consolidé
  const generateTableHTML = () => {
    if (groupedStock.length === 0) {
      return '<p style="text-align: center; font-style: italic;">Aucune donnée de stock trouvée</p>';
    }

    const tableHeaders = `
      <tr>
        <th>Catégorie</th>
        <th>Code à barre</th>
        <th>Produit</th>
        <th>Sous-produit</th>
        <th>Quantité Totale</th>
        <th>Unité</th>
        <th>Localisations</th>
        <th>Dernière Mise à Jour</th>
        <th>Statut</th>
      </tr>
    `;

    const tableRows = groupedStock.map((item, index) => {
      const isLowStock = item.totalQuantity <= 2;
      const isOutOfStock = item.totalQuantity === 0;
      const statusClass = isOutOfStock ? 'out-of-stock' : 'in-stock';
      
      return `
        <tr class="${isLowStock ? 'warning-row' : ''}">
          <td>${item.category}</td>
          <td>${item.barcode}</td>
          <td>${item.productName}</td>
          <td>${item.subProductName}</td>
          <td class="${statusClass}"><strong>${item.totalQuantity.toFixed(2)}</strong></td>
          <td>${item.unit}</td>
          <td>${item.locations.join(', ')}</td>
          <td>${item.lastUpdated ? new Date(item.lastUpdated).toLocaleDateString() : "N/A"}</td>
          <td class="${statusClass}">
            ${isOutOfStock ? 'Épuisé' : isLowStock ? 'Stock bas' : 'En stock'}
          </td>
        </tr>
      `;
    }).join('');

    return `
      <table class="stock-table">
        <thead>${tableHeaders}</thead>
        <tbody>${tableRows}</tbody>
      </table>
    `;
  };

  // Fonctions utilitaires simplifiées
  const idOf = useCallback((v) => {
    if (!v) return null;
    if (typeof v === "string") return v;
    if (typeof v === "object" && v._id) return String(v._id);
    if (typeof v === "object" && v.$oid) return String(v.$oid);
    return null;
  }, []);

  const findProductInRedux = useCallback((productId) => {
    return productsData?.find((p) => String(p._id) === String(productId)) || null;
  }, [productsData]);

  const getProductName = useCallback((productId, item) => {
    if (!productId) return "—";
    return item.productV?.name || 
           findProductInRedux(productId)?.name || 
           productId;
  }, [findProductInRedux]);

  const getSubProductName = useCallback((subProductId, item) => {
    if (!subProductId) return "—";
    return item.subProductV?.name || 
           subProductId;
  }, []);

  const getCategory = useCallback((item) => {
    return item.productV?.category || 
           findProductInRedux(idOf(item.productV))?.category || 
           "Inconnue";
  }, [findProductInRedux, idOf]);

  const getLocationName = useCallback((item) => {
    const location = item.localisation?.name || 
                    item.subProductV?.localisation?.name ||
                    item.productV?.localisation?.name;
    
    if (location) return location;
    
    const locationId = idOf(item.localisation) || 
                      idOf(item.subProductV?.localisation) ||
                      idOf(item.productV?.localisation);
    
    return locationId ? `Localisation ${locationId.substring(0, 8)}...` : "Inconnue";
  }, [idOf]);

  // Chargements des données initiales
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await axios.get("/api/Locations/getAllLocations");
        setLocations(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Erreur chargement localisations :", err);
        setLocations([]);
      }
    };
    fetchLocations();
  }, []);

  useEffect(() => {
    if (!productsData || productsData.length === 0) {
      dispatch(fetchProducts());
    }
  }, [dispatch, productsData]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await fetchStockData();
      } catch (error) {
        console.error("Erreur initiale:", error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [fetchStockData]);

  // Filtrer par localisation
  useEffect(() => {
    if (!selectedLocation) {
      const consolidated = consolidateStockData(stockData);
      const grouped = groupByProductSubProduct(consolidated);
      setGroupedStock(grouped);
      setSelectedLocationName("Toutes les localisations");
    } else {
      const location = locations.find(loc => loc._id === selectedLocation);
      setSelectedLocationName(location ? location.name : "Localisation inconnue");
      
      const filtered = stockData.filter((item) => {
        const locationId = idOf(item.localisation) || 
                          idOf(item.subProductV?.localisation) ||
                          idOf(item.productV?.localisation);
        
        return locationId === selectedLocation;
      });
      
      const consolidated = consolidateStockData(filtered);
      const grouped = groupByProductSubProduct(consolidated);
      setGroupedStock(grouped);
    }
  }, [selectedLocation, stockData, locations, consolidateStockData, groupByProductSubProduct, idOf]);

  if (loading) {
    return <div className="loading">Chargement du stock...</div>;
  }

  return (
    <div className="stock-container">
      <div ref={componentRef}>
        <div className="print-info">
          <h2>📦 Rapport de Stock Consolidé</h2>
          <p>
            <span><strong>Localisation :</strong> {selectedLocationName}</span>
            <span><strong>Date :</strong> {new Date().toLocaleDateString()}</span>
            <span><strong>Dernière MAJ :</strong> {lastUpdateTime ? lastUpdateTime.toLocaleTimeString() : 'N/A'}</span>
            <span><strong>Produits uniques :</strong> {groupedStock.length}</span>
          </p>
        </div>

        <div className="filter-section no-print">
          <label>Filtrer par localisation :</label>
          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
          >
            <option value="">🌍 Toutes les localisations</option>
            {locations.map((loc) => (
              <option key={loc._id} value={loc._id}>
                {loc.name}
              </option>
            ))}
          </select>
          
          {/* Boutons d'impression uniquement */}
          <div className="print-buttons">
            <button 
              onClick={handlePrint}
              className="print-btn no-print"
              title="Imprimer le rapport"
            >
              🖨️ Imprimer
            </button>
            
            <button 
              onClick={handleExportPDF}
              className="pdf-btn no-print"
              title="Exporter en PDF"
            >
              📄 Exporter PDF
            </button>

            <button 
              onClick={handleExportExcel}
              className="excel-btn no-print"
              title="Exporter en Excel"
            >
              📊 Exporter Excel
            </button>
          </div>
        </div>

        <table className="stock-table">
          <thead>
            <tr>
              <th>Catégorie</th>
              <th>Code à barre</th>
              <th>Produit</th>
              <th>Sous-produit</th>
              <th>Quantité Totale</th>
              <th>Unité</th>
              <th>Localisations</th>
              <th>Dernière Mise à Jour</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            {groupedStock.length > 0 ? (
              groupedStock.map((item, index) => {
                const isLowStock = item.totalQuantity <= 2;
                const isOutOfStock = item.totalQuantity === 0;
                
                return (
                  <tr key={`${item.productId}-${item.subProductId}-${index}`} className={isLowStock ? 'warning-row' : ''}>
                    <td>{item.category}</td>
                    <td>{item.barcode}</td>
                    <td><strong>{item.productName}</strong></td>
                    <td>{item.subProductName}</td>
                    <td className={isOutOfStock ? 'out-of-stock' : 'in-stock'}>
                      <strong>{item.totalQuantity.toFixed(2)}</strong>
                    </td>
                    <td>{item.unit}</td>
                    <td>{item.locations.join(', ')}</td>
                    <td>
                      {item.lastUpdated
                        ? new Date(item.lastUpdated).toLocaleDateString()
                        : "N/A"}
                    </td>
                    <td className={isOutOfStock ? 'out-of-stock' : 'in-stock'}>
                      {isOutOfStock ? '🔴 Épuisé' : 
                       isLowStock ? '🟡 Stock bas' : 
                       '🟢 En stock'}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="9" className="no-data">
                  Aucune donnée de stock trouvée
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StockReportV;